<template>
  <div class="station-queue-editor-container">
    <div class="queue-editor-list">
      <div class="queue-editor-list-scrollable">
        <div
          v-for="panel of productionPanels"
          :key="panel.id"
          @click.prevent="selectPanel(panel)"
          :class="['queue-list-panel', selectedPanelId == panel.id ? 'active' : '']"
          :ref="`panel-queue-${panel.id}`"
        >
          <div class="title">{{panelTitle(panel)}}</div>
          <div class="size">{{panel.panelHeight}} x {{panel.panelWidth}}</div>
          <div class="barcode">{{panel.barcode}}</div>
          <button class="btn btn-danger button-close" @click="removePanel($event, panel)"><i class="far fa-times"></i></button>
          <button class="btn btn-default button-info" @click="displayPanelInfo($event, panel)"><i class="far fa-info-circle"></i></button>
        </div>
      </div>
    </div>
    <div class="queue-editor-view">
      <Spinner v-if="editorLoading" />
      <div class="text-danger text-center" v-if="!editorLoading && editorLoadingError">{{editorLoadingError}}</div>
      <OrderEditorApp
        v-if="!editorLoading && !editorLoadingError && selectedOrderItem && selectedOrderItem.isEditorGeneratedItem"
        :editing="false"
        :showCloseButton="false"
        :showToolbar="false"
        :showRulers="false"
        :availableViewLevelTypes="editorAvailableViewLevelTypes"
        :orderId="selectedPanel.orderId"
        :value="viewingOrderItemList"
      />
      <div class="queue-svg-preview" v-if="!editorLoading && !editorLoadingError && selectedOrderItem && !selectedOrderItem.isEditorGeneratedItem">
        <div v-html="selectedOrderItem.svgDisplayData" />
        <div v-if="!selectedOrderItem.svgDisplayData">No Preview Available</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.station-queue-editor-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.queue-editor-list {
  position: relative;
  height: 100px;
  width: 100%;
  max-height: 100px;
  padding: 4px;
}
.queue-editor-list-scrollable {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
}
.queue-list-panel {
  cursor: pointer;
  position: relative;
  display: inline-block;
  width: 130px;
  margin-right: 10px;
  height: 80px;
  padding: 6px;
  background-color: #272727;
  border: 1px solid #202020;
  border-radius: 10px;
}
.queue-list-panel.active {
  background-color: #404040;
  border: 1px solid #000000;
}
.queue-list-panel > .title {
  font-size: 14px;
  color: #A0A0A0;
  font-weight: bold;
}
.queue-list-panel.active > .title {
  color: #FFFFFF;
}
.queue-list-panel > .size {
  color: #4da9ff;
  font-weight: 600;
}
.queue-list-panel > .barcode {
  color: #A0A0A0;
}
.queue-list-panel > .button-close {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 28px;
  height: 28px;
  padding: 0;
}
.queue-list-panel > .button-info {
  position: absolute;
  top: 38px;
  right: 4px;
  width: 28px;
  height: 28px;
  padding: 0;
}
.queue-svg-preview {
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  border: 4px solid #404040;
}
.queue-editor-view {
  flex: 1;
}
</style>

<script>
import orderItemMixin from '@/modules/orders/mixins/orderItemMixin'
import OrderEditorApp from '@glass-designer/views/app'

import { InventoryStockTypes } from '@/store/types'
import { EditorViewLevelTypes } from '@glass-designer/types'

export default {
  name: 'QueueEditorViewContainer',
  mixins: [
    orderItemMixin
  ],
  components: {
    OrderEditorApp
  },
  props: {
    productionPanels: Array,
    productionStationId: Number
  },
  data() {
    return {
      selectedPanelId: 0,
      editorLoading: false,
      editorLoadingError: '',
      order: null,
      viewingOrderItemList: [],
      selectedOrderItem: null,
      editorAvailableViewLevelTypes: [EditorViewLevelTypes.technical]
    }
  },
  computed: {
    selectedPanel() {
      if(!this.productionPanels)
        return null
      return this.productionPanels.find(panel => panel.id == this.selectedPanelId)
    }
  },
  watch: {
    productionPanels: {
      handler: function() {
        if(!this.productionPanels)
          return

        // If selected panel no longer in list, default to first panel or none
        if(!this.productionPanels.find(panel => panel.id == this.selectedPanelId))
          this.selectPanel(this.productionPanels[0])
      }
    }
  },
  mounted() {
    if(!this.productionPanels)
      return

    // If selected panel no longer in list, default to first panel
    if(!this.productionPanels.find(panel => panel.id == this.selectedPanelId) && this.productionPanels.length > 0)
      this.selectPanel(this.productionPanels[0]) 
  },
  methods: {
    selectPanel(panel) {
      if(!this.productionPanels || !panel) {
        this.selectedPanelId = null
        this.selectedOrderItem = null
        return
      }
      if(this.selectedPanelId == panel.id)
        return

      this.selectedPanelId = panel.id
      var selectedPanel = this.productionPanels.find(panel => panel.id == this.selectedPanelId)
      if(!selectedPanel)
        return

      this.editorLoading = true
      this.editorLoadingError = ''
      this.selectedOrderItem = null

      this.$apiService.ordersService.getOrder(selectedPanel.orderId)
      .then(response => {
        this.order = response.data

        // Get the glass item & child items for the specific order item we're showing
        // Ignore the rest of the order items in the order
        this.viewingOrderItemList = this.getItemGroupListForItem(this.order.orderItems, selectedPanel.orderItemId)

        // Get glass item for display of editor or SVG
        this.selectedOrderItem = this.viewingOrderItemList.find(orderItem => orderItem.stockType == InventoryStockTypes.glass)
      })
      .catch(err => {
        console.error(err)
        this.editorLoadingError = 'Error loading order'
      })
      .finally(() => {
        this.editorLoading = false
      })
    },
    removePanel(ev, panel) {
      ev.stopPropagation()
      ev.preventDefault()
      this.$emit('removePanel', panel.id)
    },
    displayPanelInfo(ev, panel) {
      ev.stopPropagation()
      ev.preventDefault()

      let panelComponent = this.$refs[`panel-queue-${panel.id}`][0]
      this.$emit('panelClick', { panel: panel, target: panelComponent, forceElement: false })
    },
    panelTitle(panel) {
      if(panel.orderItem && panel.orderItem.stock)
        return panel.orderItem.stock.code
      return ''
    }
  }

}
</script>